import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Bell,
  Plus,
  Trash2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Target,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur, getCurrencySymbol } from '@/lib/market';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon map stays in source for Tailwind/bundler visibility
const ALERT_ICON_MAP = { Target, TrendingDown, TrendingUp };

export default function AlertsPage() {
  const { user } = useAuth();
  const { alertTypeMap } = useAppData();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAlert, setNewAlert] = useState({
    symbol: '',
    alert_type: 'price_target',
    target_price: '',
    percentage: ''
  });

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const [alertsRes, holdingsRes] = await Promise.all([
        axios.get(`${API}/ai/alerts`),
        axios.get(`${API}/markets/holdings`)
      ]);
      setAlerts(alertsRes.data);
      setHoldings(holdingsRes.data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const createAlert = async () => {
    if (!newAlert.symbol) {
      toast.error('Please select a symbol');
      return;
    }

    if (newAlert.alert_type === 'price_target' && !newAlert.target_price) {
      toast.error('Please enter a target price');
      return;
    }

    if (['price_drop', 'price_rise'].includes(newAlert.alert_type) && !newAlert.percentage) {
      toast.error('Please enter a percentage');
      return;
    }

    try {
      await axios.post(`${API}/ai/alerts`, {
        symbol: newAlert.symbol,
        alert_type: newAlert.alert_type,
        target_price: newAlert.target_price ? parseFloat(newAlert.target_price) : null,
        percentage: newAlert.percentage ? parseFloat(newAlert.percentage) : null
      });
      
      toast.success('Alert created');
      setShowCreateDialog(false);
      setNewAlert({ symbol: '', alert_type: 'price_target', target_price: '', percentage: '' });
      fetchAlerts();
    } catch (err) {
      toast.error('Failed to create alert');
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/ai/alerts/${alertId}`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert deleted');
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return fmtCur(value, user?.market);
  };
  const currSym = getCurrencySymbol(user?.market);

  const getAlertDescription = (alert) => {
    if (alert.alert_type === 'price_target') {
      return `Alert when price reaches ${formatCurrency(alert.target_price)}`;
    } else if (alert.alert_type === 'price_drop') {
      return `Alert when price drops by ${alert.percentage}%`;
    } else if (alert.alert_type === 'price_rise') {
      return `Alert when price rises by ${alert.percentage}%`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#ffd87a] animate-spin mx-auto mb-4" />
          <p className="text-[#4a4a4a]">Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1a1a1a] flex items-center justify-center">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Price Alerts</h1>
            <p className="text-sm text-[#4a4a4a]">{alerts.length} active alerts</p>
          </div>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Alert
        </Button>
      </div>

      <div>
        {alerts.length > 0 ? (
          <div className="grid gap-4">
            {alerts.map((alert) => {
              const alertRow = alertTypeMap[alert.alert_type] || alertTypeMap['price_target'] || {};
              const typeInfo = { label: alertRow.label || alert.alert_type, color: alertRow.data?.color || 'bg-[#ffd87a]/20 text-[#ffd87a]' };
              const Icon = ALERT_ICON_MAP[alertRow.data?.icon_name] || Target;
              
              return (
                <Card key={alert.id} className="bg-white border-[#e5e5e5]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${typeInfo.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#1a1a1a]">{alert.symbol}</h3>
                            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                            <Badge className={alert.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[#f9f9f9]0/20 text-[#4a4a4a]'}>
                              {alert.status.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-sm text-[#4a4a4a]">{getAlertDescription(alert)}</p>
                          {alert.current_price && (
                            <p className="text-xs text-[#4a4a4a] mt-1">
                              Current price: {formatCurrency(alert.current_price)}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteAlert(alert.id)}
                        className="p-2 rounded-lg hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="bg-white border-[#e5e5e5]">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-[#f5f5f5] flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-[#4a4a4a]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a1a1a] mb-2">No Alerts Set</h3>
              <p className="text-[#4a4a4a] mb-6">Create price alerts to get notified when your holdings hit target prices</p>
              <Button onClick={() => setShowCreateDialog(true)} className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black">
                <Plus className="w-4 h-4 mr-2" /> Create First Alert
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-white/80 border-[#e5e5e5] mt-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#ffd87a] mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-[#1a1a1a] mb-1">How Price Alerts Work</h4>
                <p className="text-xs text-[#4a4a4a]">
                  Price alerts monitor your holdings and notify you when prices hit your specified targets. 
                  You can set alerts for absolute price targets or percentage changes from current prices.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Alert Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Create Price Alert</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#4a4a4a] mb-2 block">Symbol</label>
              <Select value={newAlert.symbol} onValueChange={(v) => setNewAlert({...newAlert, symbol: v})}>
                <SelectTrigger className="bg-white border-[#e5e5e5] text-[#1a1a1a]">
                  <SelectValue placeholder="Select a holding" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#e5e5e5]">
                  {holdings.map((h) => (
                    <SelectItem key={h.symbol} value={h.symbol} className="text-[#1a1a1a]">
                      {h.symbol} - {h.name?.substring(0, 30)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-[#4a4a4a] mb-2 block">Alert Type</label>
              <Select value={newAlert.alert_type} onValueChange={(v) => setNewAlert({...newAlert, alert_type: v})}>
                <SelectTrigger className="bg-white border-[#e5e5e5] text-[#1a1a1a]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#e5e5e5]">
                  <SelectItem value="price_target" className="text-[#1a1a1a]">Price Target</SelectItem>
                  <SelectItem value="price_drop" className="text-[#1a1a1a]">Price Drop %</SelectItem>
                  <SelectItem value="price_rise" className="text-[#1a1a1a]">Price Rise %</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newAlert.alert_type === 'price_target' ? (
              <div>
                <label className="text-sm text-[#4a4a4a] mb-2 block">Target Price ({currSym})</label>
                <Input
                  type="number"
                  value={newAlert.target_price}
                  onChange={(e) => setNewAlert({...newAlert, target_price: e.target.value})}
                  placeholder="Enter target price"
                  className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                />
              </div>
            ) : (
              <div>
                <label className="text-sm text-[#4a4a4a] mb-2 block">Percentage (%)</label>
                <Input
                  type="number"
                  value={newAlert.percentage}
                  onChange={(e) => setNewAlert({...newAlert, percentage: e.target.value})}
                  placeholder="Enter percentage"
                  className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-[#e5e5e5] text-[#4a4a4a]">
              Cancel
            </Button>
            <Button onClick={createAlert} className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black">
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
